import localforage from 'localforage';
import qs from 'query-string';
import { C_M, CODE_MESSAGE, IS_BROWSER } from '@/constants';
import dayjs from 'dayjs';

interface IConfig extends NextFetchRequestConfig, PostOption {
  timeout?: number;
  cache?: RequestCache;
  baseURL?: string;
}

const filterObject = (o: Record<string, any>) => {
  const r: Record<string, any> = {};
  Object.keys(o).forEach(k => {
    if (typeof o[k] !== 'undefined' && o[k] !== null) {
      r[k] = o[k];
    }
  });
  return r;
};

const HTTP_METHOD: ('get' | 'post' | 'put' | 'delete')[] = ['get', 'post', 'put', 'delete'];
const CAN_SEND_METHOD = ['post', 'put', 'delete'];
const NextFetch: Record<'get' | 'post' | 'put' | 'delete', <T, P = Record<string, any>>(_path: string, _param?: P, _config?: IConfig) => Promise<T>> = Object.create(null);
/**
 * 配置request请求时的默认参数
 */
const defaultConfig: IConfig = {
  baseURL: 'api',
  timeout: 15 * 1000,
};

HTTP_METHOD.forEach(method => {
  const canSend = CAN_SEND_METHOD.includes(method);
  NextFetch[method] = async <T, P = any>(path: string, param?: P, config?: IConfig): Promise<T> => {
    let url = path;
    const _config = Object.assign({}, defaultConfig, config);
    const headers: Record<string, any> = {
      'Content-Type': C_M.get(_config?.contentType ?? 'json'),
    };
    if (!!process.env.NEXT_PUBLIC_GRAY_TAG) {
      headers.grayTag = process.env.NEXT_PUBLIC_GRAY_TAG;
    }
    if (!path.startsWith('http')) {
      if (IS_BROWSER && !_config?.noSign) {
        let [_p] = path.split('?');
        if (_p.startsWith('/api/')) {
          _p = _p.slice(4);
        }
        const idf = (await localforage.getItem<number>(process.env.NEXT_PUBLIC_CALKEY ?? 'i_dif')) || 0;
        console.debug('idf: %d', idf);
        const { eligo } = await import('@feewee/helper-wasm');
        const _n = eligo(_p, `${Date.now() + idf}`);
        headers['x-sign'] = _n;
      }
      if (!IS_BROWSER && !config?.noSign) {
        if (process.env.NEXT_PUBLIC_SIGN) {
          headers['x-sign'] = process.env.NEXT_PUBLIC_SIGN;
        }
      }
      headers['x-appid'] = process.env.NEXT_PUBLIC_FW_APPID;
      if (_config?.baseURL == 'api') {
        url = process.env.NEXT_PUBLIC_API_HOST + url;
      }
      if (_config?.baseURL == 'api2') {
        url = process.env.NEXT_PUBLIC_FILE_HOST + url;
      }
    }
    if (_config?.token) {
      headers.Authorization = _config.token;
    }
    if (_config.ignoreAuth) {
      headers.Authorization = undefined;
      headers['x-ignore-auth'] = true;
    }
    const opts: Record<string, any> = {
      method,
      headers,
      timeout: _config.timeout,
      mode: 'cors',
      cache: _config?.cache,
    };
    if (_config?.revalidate) {
      opts.next = {
        revalidate: _config.revalidate,
        tags: _config?.tags,
      } as NextFetchRequestConfig;
    }

    if (param && !canSend) {
      url += `${url.includes('?') ? '&' : '?'}${qs.stringify(filterObject(param))}`;
    }

    if (canSend && param) {
      const _contentType = _config?.contentType ?? 'json';
      if (_contentType == 'form-urlencoded') {
        opts.body = qs.stringify(filterObject(param));
      }
      if (_contentType == 'json') {
        opts.body = JSON.stringify(filterObject(param));
      }
      if (_contentType == 'form-data') {
        if (param instanceof FormData) {
          opts.body = param;
        } else if (param) {
          const f = new FormData();
          Object.entries(param).forEach(([key, value]: any[]) => {
            f.append(key, value);
          });
          opts.body = f;
        }
      }
    }
    const response = await fetch(url, opts);
    let data: TResponse<T> = {} as TResponse<T>;
    try {
      data = await response.json();
    } catch (e) {}
    // 统一约定json格式
    if (response.status === 200) {
      if (!IS_BROWSER) {
        console.info('========================================request========================================');
        console.info(
          '[%s] API: %s \ntoken: %s \nresponse: %s',
          dayjs().format('YYYY-MM-DD HH:mm:ss.SSS'),
          url,
          headers?.Authorization,
          JSON.stringify(data).slice(0, 500) + (JSON.stringify(data).length > 500 ? '...' : ''),
        );
        console.info('\n');
      }
      if (typeof data?.success !== 'boolean') {
        if (config?.intactResponse) {
          return (data ?? response) as T;
        }
        return (data?.data ?? data ?? response) as T;
      }
      if (data?.success) {
        if (config?.intactResponse) {
          return (data ?? response) as T;
        }
        return data?.data as T;
      }
      const code = data?.code || response.status;
      const message = data?.result || CODE_MESSAGE[code] || response.statusText;
      const error = new Error(message);
      // @ts-expect-error 忽略这个问题
      error.code = code;
      return Promise.reject(error);
    }
    if (!IS_BROWSER) {
      console.info('========================================request========================================');
      console.info('[%s] API: %s \ntoken: %s \n%s %s', dayjs().format('YYYY-MM-DD HH:mm:ss.SSS'), url, headers?.Authorization, response.status, response.statusText);
      console.info('\n');
    }
    const code = data?.code || response.status;
    const message = data?.result || CODE_MESSAGE[code] || response.statusText;
    const error = new Error(message);
    // @ts-expect-error 忽略这个问题
    error.code = code;

    return Promise.reject(error);
  };
});

export default NextFetch;
